<template>
  <div class="py-300 next-action-suggestions-list">
    <div class="title-container">
      <h3 class="title">
        <BIcon
          rounded
          class="mr-100"
        >
          tv_options_edit_channels 
        </BIcon>
        <span>{{ $t('nextActionSuggestion.title') }}</span>
      </h3>
      <div class="number-of-items">
        {{ numberOfItems }}
      </div>
      <SelectUser
        v-model="selectedOwnerId"
        :placeholder="$t('nextActionSuggestion.filter.ownerPlaceholder')"
        class="owner-select"
        clearable
        @update:model-value="handleOwnerChange"
      />
    </div>
    <BCard
      v-loading="isLoading"
      class="suggestions-card"
    >
      <div 
        class="suggestions-content"
        :class="{ 'fixed-height': shouldUseFixedHeight }"
      >
        <template v-if="!isLoading">
          <template v-if="nextActionSuggestions.length === 0">
            <div class="empty-box">
              {{ $t('sisense.empty') }}
            </div>
          </template>
          <template v-else>
            <NextActionSuggestion
              v-for="suggestion in nextActionSuggestions"
              :key="suggestion.id"
              :next-action="suggestion"
            />
          </template>
        </template>
      </div>
    </BCard>
    <div
      v-if="expandable && !isLoading"
      class="expand-button"
      @click="toggleExpanded"
    >
      <BBtn
        variant="outlined"
        size="mini"
      >
        <BIcon
          class="expand-icon"
          size="small"
        >
          {{ isExpanded ? 'unfold_less' : 'unfold_more' }}
        </BIcon>
        {{ isExpanded ? $t('general.listCollapse.collapse') : $t('general.listCollapse.expand') }}
      </BBtn>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { NextActionSuggestion as TNextActionSuggestion } from '@/api/openapi';
import { NextActionSuggestionApiService } from '@/api/user/resources/next_action_suggestion';
import { useCurrentUser } from '@/composable/user/user/users';
import { useWait } from '@/composable/vue-wait';
import SelectUser from '@/plugins/biscuet-materials/components/molecules/SelectUser.vue';
import NextActionSuggestion from './NextActionSuggestion.vue';

const { doActionWithWait, wait } = useWait();
const { currentUser, fetchCurrentUserPromise } = useCurrentUser();

const { t } = useI18n();
const api = new NextActionSuggestionApiService();
const nextActionSuggestions = ref<TNextActionSuggestion[]>([]);
const totalCount = ref<number>(0);
const selectedOwnerId = ref<number | undefined>();
const isExpanded = ref<boolean>(false);

const NUMBER_OF_COLLAPSED_ITEMS = 3; // 折りたたみ時の表示件数
const NUMBER_OF_EXPANDED_ITEMS = 8; // 展開時の表示件数

const isLoading = computed(() => {
  return wait.is('fetchNextActionSuggestions');
});
const numberOfItems = computed(() => {
  if (totalCount.value > nextActionSuggestions.value.length) {
    return `${nextActionSuggestions.value.length} / ${t('general.numberOf', { number: totalCount.value })}`;
  }
  return t('general.numberOf', { number: nextActionSuggestions.value.length });
});

// 開閉機能
const expandable = computed(() => {
  return nextActionSuggestions.value.length > NUMBER_OF_COLLAPSED_ITEMS;
});
const numberOfMaxVisibleItems = computed(() => {
  return isExpanded.value ? NUMBER_OF_EXPANDED_ITEMS : NUMBER_OF_COLLAPSED_ITEMS;
});
const shouldUseFixedHeight = computed(() => {
  return nextActionSuggestions.value.length > numberOfMaxVisibleItems.value;
});

onBeforeMount(async () => {
  await fetchCurrentUserPromise;
  // デフォルトでログインユーザーを選択
  selectedOwnerId.value = currentUser.value?.id;
  await fetchNextActionSuggestions();
});

// データが変更されたときは開閉状態をリセット
watch(nextActionSuggestions, () => {
  isExpanded.value = false;
});

const fetchNextActionSuggestions = async () => {
  await doActionWithWait('fetchNextActionSuggestions', async () => {
    const { data } = await api.getNextActionSuggestions({
      request: {
        ownerId: selectedOwnerId.value,
      },
    });
    nextActionSuggestions.value = data.nextActionSuggestions || [];
    totalCount.value = data.meta?.totalCount || 0;
  });
};

const handleOwnerChange = async () => {
  await fetchNextActionSuggestions();
};

const toggleExpanded = () => {
  isExpanded.value = !isExpanded.value;
};
</script>

<style lang="scss" scoped>
$item-height: 74px;
.next-action-suggestions-list {
  .title-container {
    @include m-flex-align-center;
    // justify-content: space-between;
    .title {
      @include m-flex-align-center;
      margin-right: $basespace-400;
    }
    .number-of-items {
      @include m-flex-align-center;
      margin-right: $basespace-300;
      font-weight: $font-weight-bold;
    }
    .owner-select {
      width: 200px;
    }
  }

  .expand-button {
    @include m-flex-align-center;
    @include m-flex-center;
    padding-top: $basespace-200;

    .expand-icon {
      margin-left: $basespace-50;
      transition: transform 0.3s ease-in-out;
    }
  }

  .suggestions-card {
    padding: 0;
    margin-top: $basespace-200;
  }

  .suggestions-content {
    .empty-box {
      padding: $basespace-300 $basespace-600;
    }
    &.fixed-height {
      max-height: calc($item-height * v-bind(numberOfMaxVisibleItems) + $basespace-100);
      transition: max-height 0.3s ease-in-out;
      overflow-y: auto;
    }
    :deep(.task-container) {
      padding: $basespace-300 $basespace-600;
      border-bottom: 1px solid $bdcolor-light;
      &:last-child {
        border-bottom: none;
      }
    }
  }
}
</style>
